import React, { useMemo, useState } from 'react';

import { useDispatch } from 'react-redux';

import Box from '@amzn/meridian/box';
import ConfirmModal from 'src/components/shared/confirmModal/confirmModal';
import {
  setJmrResults,
  setPageProgress,
  setSelectedTaskNames,
  setSelectedTasks,
} from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { type BackgroundFormData } from 'src/store/newJmrPageSlice/types';
import { useAppSelector } from 'src/store/store';
import { datePickerStringToDateObj } from 'src/utils/dateUtils';
import { JmrFlowTypes, JmrPageProgress } from 'src/utils/jmrUtils';

import { SectionTitle, SectionWrapper } from '../common';

/**
 * Background Review
 */
const BackgroundReview = () => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { backgroundFormData, jmrFlowType } = useAppSelector((state) => state.newJmrPage);
  const backgroundReviewRows = useMemo(() => getBackgroundReviewRows(backgroundFormData), [backgroundFormData]);

  const goToFirstPage = () => {
    dispatch(setPageProgress(JmrPageProgress.Background));
    dispatch(setSelectedTasks([]));
    dispatch(setSelectedTaskNames([]));
    dispatch(setJmrResults([]));
  };

  return (
    <Box>
      {/* Main Body */}
      <SectionTitle
        label="Background"
        editButtonDisabled={jmrFlowType === JmrFlowTypes.AlternativePlacementRecommended}
        editButtonOnClick={() => setIsModalOpen(true)}
      />
      <SectionWrapper label="Personal Information" items={backgroundReviewRows} />

      {/* Modal */}
      <ConfirmModal
        modalTitle="Leave page"
        modalDescription="Leaving this page will restart your Job Match Results process."
        open={isModalOpen}
        setOpen={() => setIsModalOpen(false)}
        onConfirm={() => goToFirstPage()}
        width="419px"
      />
    </Box>
  );
};

/**
 * Setup the rows for the background form data.
 */
const getBackgroundReviewRows = (data: BackgroundFormData) => [
  {
    label: 'Associate Name',
    value: data.fullName,
  },
  {
    label: 'Employee ID',
    value: data.employeeID,
  },
  {
    label: 'Person ID',
    value: data.personId,
  },
  {
    label: 'Job',
    value: data.job,
  },
  {
    label: 'Location',
    value: data.siteLocation,
  },
  {
    label: 'Shift',
    value: data.shift,
  },
  {
    label: 'Shift',
    value: `${data.shift} | ${data.shiftLength} hours`,
  },
  {
    label: 'Case Information',
    value: `${data.caseSource} | ${data.caseID}`,
  },
  {
    label: "Associate's Manager",
    value: data.managerName,
  },
  {
    label: 'Restriction Duration',
    value:
      data.indefiniteRestrictions && data.site.participatingInDecisionHubPilot
        ? `${dateToStr(data.restrictionStartDate)} - indefinite`
        : `${dateToStr(data.restrictionStartDate)} - ${dateToStr(data.restrictionEndDate)}`,
  },
];

/**
 * Shorten the date to string function.
 */
const dateToStr = (value: string) => datePickerStringToDateObj(value).toLocaleDateString('en-US');

// **** Export Default **** //

export default BackgroundReview;
